import {MEDIOCRE_TOTAL_IMAGE_PADDING} from 'notebook/mediocre/MediocreConstants';
import {hasNLO} from 'notebook/TypePredicates';
import {NotePart} from 'notebook/util/NoteUtilConstants';

import {
  GRID_CONTAINED_IMAGE_PADDING,
  isImageFileNotePart,
  isImageNotePartGrid,
  isNotePartGrid,
  MIN_ITEM_WIDTH,
} from '../NotePartGridGroupConstants';
import {DEFAULT_GRID_PLACEHOLDER_WIDTH} from './imageTypeUtils';

export const EXTERNAL_IMAGE_GRID_PASTE_MESSAGE =
  'Only existing Benchling images can be pasted into an image grid. Use Upload to add new images.';
export const INCOMPATIBLE_IMAGE_GRID_PASTE_MESSAGE = 'Only images can be pasted into an image grid.';

/**
 * Return copies of the image note parts represented by a clipboard payload, or null when any
 * top-level item is incompatible with an image grid. A copied image within a grid is serialized as
 * a one-image NotePartGrid, while a copied standalone image is serialized as a NoteLinkedFile.
 * Empty grids contribute no images and are ignored when the payload also contains images.
 *
 * Standalone images and grid images store the width of containers with different amounts of
 * surrounding chrome. Adjust explicit standalone widths so the image itself remains the same
 * displayed size in the grid. Older/unresized note parts may not have a width, so those use the
 * same default as newly uploaded grid images.
 */
export function getImagesForImageGridPaste(items: Array<NotePart>): Array<NotePart> | null {
  const images: Array<NotePart> = [];

  for (const item of items) {
    if (isImageFileNotePart(item)) {
      const imageWithAdjustedWidth =
        item.itemWidth == null
          ? item
          : {
              ...item,
              itemWidth: Math.max(
                MIN_ITEM_WIDTH,
                item.itemWidth - MEDIOCRE_TOTAL_IMAGE_PADDING + GRID_CONTAINED_IMAGE_PADDING
              ),
            };
      images.push(imageWithAdjustedWidth);
    } else if (hasNLO(item) && isNotePartGrid(item.item)) {
      if (item.item.containedNoteParts.length === 0) {
        continue;
      }
      if (!isImageNotePartGrid(item.item)) {
        return null;
      }
      images.push(...item.item.containedNoteParts);
    } else {
      return null;
    }
  }

  if (images.length === 0) {
    return null;
  }

  return images.map((image) => ({
    ...image,
    itemWidth: image.itemWidth ?? DEFAULT_GRID_PLACEHOLDER_WIDTH,
  }));
}
